import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Clock,
  CheckCircle,
  XCircle,
  UserCheck,
  UserX,
  AlertCircle,
  Mail,
  Phone,
  Building2
} from 'lucide-react';
import { Layout } from '../components/Layout/Layout';
import { useAdvisorStore } from '../store/advisorStore';
import { AdvisorApprovalModal } from '../components/Admin/AdvisorApprovalModal';
import { AdvisorRejectionModal } from '../components/Admin/AdvisorRejectionModal';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';
import type { AdvisorRequest } from '../types/advisor';

export const AdvisorRequestsPage: React.FC = () => {
  const navigate = useNavigate();
  const {
    requests,
    isLoading,
    error,
    fetchAdvisorRequests,
    fetchStats,
    fetchTerritories,
    clearError
  } = useAdvisorStore();

  const [selectedRequest, setSelectedRequest] = React.useState<AdvisorRequest | null>(null);
  const [showApprovalModal, setShowApprovalModal] = React.useState(false);
  const [showRejectionModal, setShowRejectionModal] = React.useState(false);
  const [filter, setFilter] = React.useState<'ALL' | 'PENDING' | 'APPROVED' | 'REJECTED'>('PENDING');

  useEffect(() => {
    fetchAdvisorRequests();
    fetchStats();
    fetchTerritories();
    clearError();
  }, []);

  const handleApprove = (request: AdvisorRequest) => {
    setSelectedRequest(request);
    setShowApprovalModal(true);
  };

  const handleReject = (request: AdvisorRequest) => {
    setSelectedRequest(request);
    setShowRejectionModal(true);
  };

  const handleModalClose = () => {
    setShowApprovalModal(false);
    setShowRejectionModal(false);
    setSelectedRequest(null);
  };

  const handleSuccess = () => {
    fetchAdvisorRequests();
    fetchStats();
    handleModalClose();
  };

  const filteredRequests = filter === 'ALL'
    ? requests
    : requests.filter(req => req.status.toUpperCase() === filter);

  const getStatusBadge = (status: string) => {
    const statusMap = {
      'PENDING': { label: 'En attente', color: 'bg-orange-100 text-orange-800', icon: Clock },
      'APPROVED': { label: 'Approuvée', color: 'bg-green-100 text-green-800', icon: CheckCircle },
      'REJECTED': { label: 'Refusée', color: 'bg-red-100 text-red-800', icon: XCircle }
    };

    const statusInfo = statusMap[status.toUpperCase() as keyof typeof statusMap] || statusMap.PENDING;
    const Icon = statusInfo.icon;

    return (
      <span className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${statusInfo.color}`}>
        <Icon className="w-3 h-3" />
        <span>{statusInfo.label}</span>
      </span>
    );
  };

  if (error) {
    return (
      <Layout>
        <div className="p-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
              <div>
                <p className="text-red-800 font-medium">Erreur de chargement</p>
                <p className="text-red-600 text-sm mt-1">{error}</p>
                <button
                  onClick={() => {
                    clearError();
                    fetchAdvisorRequests();
                  }}
                  className="mt-3 text-red-700 hover:text-red-800 font-medium text-sm"
                >
                  Réessayer
                </button>
              </div>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/admin/accompagnateurs')}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Demandes d'accompagnateur</h1>
              <p className="text-gray-600 mt-1">
                Gérer les candidatures des nouveaux accompagnateurs
              </p>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setFilter('ALL')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                filter === 'ALL'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Toutes ({requests.length})
            </button>
            <button
              onClick={() => setFilter('PENDING')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                filter === 'PENDING'
                  ? 'bg-orange-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              En attente ({requests.filter(r => r.status.toUpperCase() === 'PENDING').length})
            </button>
            <button
              onClick={() => setFilter('APPROVED')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                filter === 'APPROVED'
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Approuvées ({requests.filter(r => r.status.toUpperCase() === 'APPROVED').length})
            </button>
            <button
              onClick={() => setFilter('REJECTED')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                filter === 'REJECTED'
                  ? 'bg-red-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Refusées ({requests.filter(r => r.status.toUpperCase() === 'REJECTED').length})
            </button>
          </div>
        </div>

        {/* Requests List */}
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filteredRequests.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center">
            <Clock className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Aucune demande</h3>
            <p className="text-gray-500">
              {filter === 'PENDING'
                ? 'Aucune demande en attente pour le moment.'
                : `Aucune demande ${filter === 'APPROVED' ? 'approuvée' : 'refusée'}.`
              }
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredRequests.map((request) => (
              <div
                key={request.id}
                className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition-all"
              >
                <div className="flex items-start justify-between">
                  {/* Request Info */}
                  <div className="flex-1 space-y-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center space-x-3">
                          <h3 className="text-lg font-semibold text-gray-900">
                            {request.full_name}
                          </h3>
                          {getStatusBadge(request.status)}
                        </div>
                        <p className="text-sm text-gray-500 mt-1">
                          Demande reçue {formatDistanceToNow(new Date(request.created_at), {
                            addSuffix: true,
                            locale: fr
                          })}
                        </p>
                      </div>
                    </div>

                    {/* Contact Info */}
                    <div className="flex flex-wrap gap-4">
                      <div className="flex items-center space-x-2 text-sm text-gray-600">
                        <Mail className="w-4 h-4" />
                        <span>{request.email}</span>
                      </div>
                      {request.phone && (
                        <div className="flex items-center space-x-2 text-sm text-gray-600">
                          <Phone className="w-4 h-4" />
                          <span>{request.phone}</span>
                        </div>
                      )}
                      {request.organization && (
                        <div className="flex items-center space-x-2 text-sm text-gray-600">
                          <Building2 className="w-4 h-4" />
                          <span>{request.organization}</span>
                        </div>
                      )}
                    </div>

                    {/* Experience & Motivation */}
                    {request.experience && (
                      <div>
                        <h4 className="text-sm font-medium text-gray-700 mb-1">Expérience</h4>
                        <p className="text-sm text-gray-600">{request.experience}</p>
                      </div>
                    )}
                    {request.motivation && (
                      <div>
                        <h4 className="text-sm font-medium text-gray-700 mb-1">Motivation</h4>
                        <p className="text-sm text-gray-600">{request.motivation}</p>
                      </div>
                    )}

                    {/* Processing Info */}
                    {request.processed_at && request.processor && (
                      <div className="pt-4 border-t border-gray-100">
                        <p className="text-sm text-gray-500">
                          Traitée par <span className="font-medium text-gray-700">{request.processor.full_name}</span> le{' '}
                          {new Date(request.processed_at).toLocaleDateString('fr-FR', {
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric'
                          })}
                        </p>
                        {request.notes && (
                          <p className="text-sm text-gray-600 mt-1">Note: {request.notes}</p>
                        )}
                        {request.rejection_reason && (
                          <p className="text-sm text-red-600 mt-1">Raison du refus: {request.rejection_reason}</p>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  {request.status.toUpperCase() === 'PENDING' && (
                    <div className="flex items-center space-x-2 ml-4">
                      <button
                        onClick={() => handleApprove(request)}
                        className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                        title="Approuver"
                      >
                        <UserCheck className="w-4 h-4" />
                        <span>Approuver</span>
                      </button>
                      <button
                        onClick={() => handleReject(request)}
                        className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                        title="Refuser"
                      >
                        <UserX className="w-4 h-4" />
                        <span>Refuser</span>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Modals */}
        {selectedRequest && (
          <>
            <AdvisorApprovalModal
              isOpen={showApprovalModal}
              onClose={handleModalClose}
              advisor={selectedRequest as any}
              onSuccess={handleSuccess}
            />

            <AdvisorRejectionModal
              isOpen={showRejectionModal}
              onClose={handleModalClose}
              advisor={selectedRequest as any}
              onSuccess={handleSuccess}
            />
          </>
        )}
      </div>
    </Layout>
  );
};
